// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xHeaderActions } from "@bg/App/Store/Reducers";
import Button from "@bg/MobiusOverrides/Button/Button";
import Check from "@bg/XDL/Icons/Check";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

export interface RegisterNowStyles {
    registrationDescription?: InjectableCss;
    registrationBenefits?: InjectableCss;
    registrationButton?: InjectableCss;
    checkoutAsGuestButton?: InjectableCss;
}

export const registerNowStyles: RegisterNowStyles = {
    registrationButton: {
        css: css`
            width: 100%;
        `,
    },
    checkoutAsGuestButton: {
        css: css`
            width: 100%;
            margin-top: 12px;
        `,
    },
    registrationDescription: {
        css: css`
            padding-bottom: 13px;
            font-weight: ${fontConfig.weight.semiBold};
        `,
    },
    registrationBenefits: {
        css: css`
            padding-bottom: 23px;
            ul {
                padding-left: 0;
                li {
                    padding-left: 0;
                    display: flex;
                    align-items: center;
                    ::before {
                        content: none !important;
                    }
                    svg {
                        width: 18px;
                        margin-right: 6px;
                        height: 18px;
                        color: ${getColor("primary")};
                    }
                }
            }
        `,
    },
};

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    benefits = "benefits",
    benefitsTitle = "benefitsTitle",
}

export interface CustomBenefitModel {
    fields: {
        benefit: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.benefits]: CustomBenefitModel[];
        [fields.benefitsTitle]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const { search } = getLocation(state);
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    const accountSettings = getSettingsCollection(state).accountSettings;
    const returnUrl = getReturnUrl(state);
    const checkoutShippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromCheckoutPage = returnUrl === checkoutShippingPageUrl || returnUrl === onePageCheckoutPageUrl;
    return {
        allowCreateAccount: accountSettings.allowCreateAccount,
        allowGuestCheckout:
            accountSettings.allowGuestCheckout && !getCurrentUserIsGuest(state) && referredFromCheckoutPage,
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    setHeaderSignInDrawerOpen: (open: boolean) => (dispatch: Dispatch<xHeaderActions>) => {
        dispatch({ type: "xHeader/headerSignInDrawerOpen", open });
    },
    signInAsGuest,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;
const styles = registerNowStyles;
const RegisterNow = ({
    fields,
    createAccountUrl,
    setHeaderSignInDrawerOpen,
    returnUrl,
    signInAsGuest,
    allowGuestCheckout,
}: Props) => {
    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    return (
        <StyledWrapper className="register-wrapper">
            <Typography as="p" {...styles.registrationDescription}>
                {fields.benefitsTitle || ""}
            </Typography>
            <StyledWrapper {...styles.registrationBenefits}>
                <UnorderedList>
                    {fields.benefits &&
                        fields.benefits.map((item: CustomBenefitModel, index: any) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <ListItem key={index}>
                                <Check />
                                {item.fields.benefit}
                            </ListItem>
                        ))}
                </UnorderedList>
            </StyledWrapper>

            {createAccountUrl && (
                <Button
                    color="primary"
                    {...styles.registrationButton}
                    href={createAccountUrl}
                    onClick={() => {
                        setTimeout(() => {
                            setHeaderSignInDrawerOpen(false);
                        }, 300);
                    }}
                >
                    {translate("Register Now")}
                </Button>
            )}
            {allowGuestCheckout && (
                <Button color="secondary" {...styles.checkoutAsGuestButton} onClick={handleCheckoutAsGuest}>
                    {translate("Checkout as Guest")}
                </Button>
            )}
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Register Now",
    fieldDefinitions: [
        {
            name: fields.benefitsTitle,
            displayName: "Benefits Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.benefits,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields) => {
                return item.fields.benefit;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    displayName: "Benefit Title",
                    name: "benefit",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: false,
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RegisterNow),
    definition,
};

export default widgetModule;
